import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Download,
  Eye,
  Shield,
  Calendar,
  Clock,
  Building2,
  FileText,
  History,
  Upload,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { formatBackendDate } from '../../lib/dateUtils';
import API_BASE from '../../utils/apiBase';
import { trainingCertificateAccessErrorMessage } from '../../lib/trainingCertificateAccessError';

const API = API_BASE;

// Status styling
const STATUS_STYLES = {
  current: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  expiring_soon: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  expired: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  missing: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' },
  pending: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
};

/**
 * TrainingDetailDrawer - Shows full training record details
 * 
 * Opens when clicking a row in TrainingMatrix.
 * Displays:
 * - Training info (name, provider, dates, status)
 * - Certificate/evidence files with view/download
 * - Verification status and actions (admin only)
 * - History of changes
 */
export default function TrainingDetailDrawer({
  isOpen,
  onClose,
  trainingItem,
  employeeId,
  isAdmin = false,
  onUpdate
}) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');
  // P0 FIX: Add state for inline document preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  
  // P0 FIX: Re-extraction state
  const [reExtractLoading, setReExtractLoading] = useState(false);
  const [reExtractedTrainings, setReExtractedTrainings] = useState(null);
  const [showReExtractPreview, setShowReExtractPreview] = useState(false);
  const [selectedReExtracted, setSelectedReExtracted] = useState([]);
  
  // Never carry one person's old-file failure into another training record.
  useEffect(() => {
    setPreviewError(null);
    setPreviewUrl(null);
    setPreviewType(null);
  }, [isOpen, trainingItem?.record_id]);

  // Fetch history when tab changes
  const fetchHistory = async () => {
    if (!trainingItem?.record_id) return;
    
    setHistoryLoading(true);
    try {
      const response = await axios.get(
        `${API}/training-records/${trainingItem.record_id}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Backend returns { training_record, history, total_corrections }
      setHistory(response.data?.history || []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setHistory([]); // Reset to empty array on error
    } finally {
      setHistoryLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'history' && history.length === 0) {
      fetchHistory();
    }
    // Clear preview when switching tabs
    if (tab !== 'evidence') {
      setPreviewUrl(null);
      setPreviewType(null);
      setPreviewError(null);
    }
  };
  
  // P0 FIX: View certificate inline instead of new tab
  const handleViewCertificate = async () => {
    if (!trainingItem?.record_id) return;
    
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const response = await axios.get(
        `${API}/training-records/${trainingItem.record_id}/certificate/file`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      
      // Determine preview type
      if (contentType.includes('pdf')) {
        setPreviewType('pdf');
      } else if (contentType.includes('image')) {
        setPreviewType('image');
      } else {
        // Fallback to opening in new tab for unsupported types
        window.open(url, '_blank');
        return;
      }
      
      setPreviewUrl(url);
      setPreviewError(null);
    } catch (err) {
      const message = trainingCertificateAccessErrorMessage(err.response?.status);
      setPreviewUrl(null);
      setPreviewType(null);
      setPreviewError(message);
      toast.error(message);
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Download certificate
  const handleDownloadCertificate = async () => {
    if (!trainingItem?.record_id) return;
    
    try {
      const response = await axios.get(
        `${API}/training-records/${trainingItem.record_id}/certificate/download`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${trainingItem.code}_certificate.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificate downloaded');
    } catch (err) {
      const message = trainingCertificateAccessErrorMessage(err.response?.status);
      setPreviewError(message);
      toast.error(message);
    }
  };
  
  // P0 FIX: Re-extract training data from existing certificate
  const handleReExtract = async () => {
    // Get certificate URL from training item
    const certUrl = trainingItem?.certificate_url || trainingItem?.source_document_url;
    const docId = trainingItem?.source_document_id || trainingItem?.certificate_document_id;
    
    if (!certUrl && !docId) {
      toast.error('No certificate file found for this training record');
      return;
    }
    
    setReExtractLoading(true);
    try {
      const response = await axios.post(
        `${API}/employees/${employeeId}/training/re-extract`,
        { 
          certificate_url: certUrl,
          document_id: docId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.trainings?.length > 0) {
        setReExtractedTrainings(response.data);
        setSelectedReExtracted(response.data.trainings.map((_, idx) => idx));
        setShowReExtractPreview(true);
        toast.success(`Re-extracted ${response.data.total_extracted} training(s)`);
      } else {
        toast.error(response.data.message || 'No trainings detected');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-extraction failed');
    } finally {
      setReExtractLoading(false);
    }
  };
  
  // P0 FIX: Save re-extracted trainings
  const handleSaveReExtracted = async () => {
    if (!reExtractedTrainings || selectedReExtracted.length === 0) return;
    
    // Inject document_id from the source training item into each re-extracted training
    const docId = trainingItem?.source_document_id || trainingItem?.certificate_document_id;
    const trainingsToSave = selectedReExtracted.map(idx => ({
      ...reExtractedTrainings.trainings[idx],
      document_id: docId
    }));
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/employees/${employeeId}/training/bulk-save`,
        { trainings: trainingsToSave },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Submitted ${trainingsToSave.length} training item(s) for review`);
      setShowReExtractPreview(false);
      setReExtractedTrainings(null);
      setSelectedReExtracted([]);
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit trainings for review');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle selection of re-extracted training
  const toggleReExtractSelection = (idx) => {
    setSelectedReExtracted(prev => 
      prev.includes(idx) 
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    );
  };
  
  // Verify training
  const handleVerify = async () => {
    if (!trainingItem?.record_id) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/training-records/${trainingItem.record_id}/verify`,
        { notes: verifyNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Training verified');
      setVerifyNotes('');
      onUpdate?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to verify');
    } finally {
      setLoading(false);
    }
  };
  
  // Unverify training
  const handleUnverify = async () => {
    if (!trainingItem?.record_id) return;
    
    setLoading(true);
    try {
      await axios.post(
        `${API}/training-records/${trainingItem.record_id}/unverify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Verification removed');
      onUpdate?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unverify');
    } finally {
      setLoading(false);
    }
  };
  
  if (!trainingItem) return null;
  
  const statusStyle = STATUS_STYLES[trainingItem.status] || STATUS_STYLES.missing;
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="training-detail-drawer">
        <SheetHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl">{trainingItem.title}</SheetTitle>
            {/* P0 FIX: Re-extract button */}
            {isAdmin && (trainingItem.certificate_url || trainingItem.source_document_id) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReExtract}
                disabled={reExtractLoading}
                data-testid="re-extract-btn"
              >
                {reExtractLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <History className="h-4 w-4 mr-1" />
                    Re-extract
                  </>
                )}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="font-mono text-xs">
              {trainingItem.code}
            </Badge>
            <Badge 
              variant="outline" 
              className={cn(statusStyle.bg, statusStyle.text, statusStyle.border)}
            >
              {trainingItem.status?.replace(/_/g, ' ')}
            </Badge>
            {trainingItem.verified && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                <Shield className="h-3 w-3 mr-1" />
                Verified
              </Badge>
            )}
          </div>
          {/* Show verified by if available */}
          {trainingItem.verified_by && (
            <p className="text-xs text-gray-500 mt-1">
              Verified by: {trainingItem.verified_by}
              {trainingItem.verified_at && ` on ${formatBackendDate(trainingItem.verified_at, { format: 'short' })}`}
            </p>
          )}
        </SheetHeader>
        
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="evidence">Evidence</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          
          {/* Details Tab */}
          <TabsContent value="details" className="mt-4 space-y-4">
            {/* Status Card */}
            <div className={cn("p-4 rounded-lg border", statusStyle.bg, statusStyle.border)}>
              <div className="flex items-center gap-3">
                {trainingItem.status === 'current' || trainingItem.status === 'completed' ? (
                  <CheckCircle2 className={cn("h-6 w-6", statusStyle.text)} />
                ) : trainingItem.status === 'expired' ? (
                  <XCircle className={cn("h-6 w-6", statusStyle.text)} />
                ) : trainingItem.status === 'expiring_soon' ? (
                  <AlertTriangle className={cn("h-6 w-6", statusStyle.text)} />
                ) : (
                  <Clock className={cn("h-6 w-6", statusStyle.text)} />
                )}
                <div>
                  <p className={cn("font-medium", statusStyle.text)}>
                    {trainingItem.status === 'current' ? 'Training Current' :
                     trainingItem.status === 'completed' ? 'Completed' :
                     trainingItem.status === 'expired' ? 'Training Expired' :
                     trainingItem.status === 'expiring_soon' ? 'Renewal Required Soon' :
                     'Training Not Completed'}
                  </p>
                  {trainingItem.detail && (
                    <p className={cn("text-sm", statusStyle.text, "opacity-80")}>
                      {trainingItem.detail}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              {trainingItem.completed_at && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Completed</p>
                  <p className="font-medium flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    {formatBackendDate(trainingItem.completed_at, { format: 'medium' })}
                  </p>
                </div>
              )}
              
              {trainingItem.expires_at && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Expires</p>
                  <p className={cn(
                    "font-medium flex items-center gap-1.5",
                    trainingItem.status === 'expired' ? 'text-red-600' :
                    trainingItem.status === 'expiring_soon' ? 'text-amber-600' : ''
                  )}>
                    <Clock className="h-4 w-4" />
                    {formatBackendDate(trainingItem.expires_at, { format: 'medium' })}
                  </p>
                </div>
              )}
              
              {trainingItem.provider && (
                <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Provider</p>
                  <p className="font-medium flex items-center gap-1.5">
                    <Building2 className="h-4 w-4 text-gray-400" />
                    {trainingItem.provider}
                  </p>
                </div>
              )}
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Validity Period</p>
                <p className="font-medium">
                  {trainingItem.validity_days ? `${trainingItem.validity_days} days` : 'Lifetime'}
                </p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Work Blocking</p>
                <p className={cn(
                  "font-medium",
                  trainingItem.blocker ? 'text-red-600' : 'text-gray-600'
                )}>
                  {trainingItem.blocker ? 'Yes - Required' : 'No'}
                </p>
              </div>
            </div>
            
            {/* Admin Verification Section */}
            {isAdmin && trainingItem.has_evidence && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-medium text-blue-800 mb-3">Admin Verification</p>
                
                {trainingItem.verified ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-emerald-600" />
                      <span className="text-emerald-700">Verified</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleUnverify}
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove Verification'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Textarea
                      placeholder="Verification notes (optional)..."
                      value={verifyNotes}
                      onChange={(e) => setVerifyNotes(e.target.value)}
                      rows={2}
                      className="bg-white"
                    />
                    <Button
                      onClick={handleVerify}
                      disabled={loading}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Shield className="h-4 w-4 mr-2" />
                      )}
                      Verify Training
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Evidence Tab */}
          <TabsContent value="evidence" className="mt-4">
            {previewError && (
              <div role="alert" data-testid="training-certificate-recovery-warning" className="mb-4 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">Certificate evidence needs attention</p>
                    <p className="text-sm mt-1">{previewError}</p>
                    <p className="text-xs mt-2">Review the History tab and available copies. A failed preview does not mean the certificate expired or the original was deleted.</p>
                  </div>
                </div>
              </div>
            )}
            {/* P0 FIX: Inline Preview Panel */}
            {previewUrl && (
              <div className="mb-4 border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between p-2 bg-gray-100 border-b">
                  <span className="text-sm font-medium text-gray-700">Certificate Preview</span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(previewUrl, '_blank')}
                      className="h-7 px-2"
                    >
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      Open in Tab
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setPreviewUrl(null);
                        setPreviewType(null);
                      }}
                      className="h-7 px-2"
                    >
                      Close
                    </Button>
                  </div>
                </div>
                <div className="bg-gray-900 flex items-center justify-center" style={{ minHeight: '350px', maxHeight: '450px' }}>
                  {previewType === 'image' ? (
                    <img 
                      src={previewUrl} 
                      alt="Training Certificate" 
                      className="max-w-full max-h-[450px] object-contain"
                    />
                  ) : previewType === 'pdf' ? (
                    <iframe 
                      src={previewUrl}
                      title="Training Certificate PDF"
                      className="w-full h-[450px]"
                      style={{ border: 'none' }}
                    />
                  ) : null}
                </div>
              </div>
            )}
            
            {trainingItem.has_evidence || trainingItem.evidence?.length > 0 || trainingItem.source_document_id || trainingItem.record_id ? (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">Training Certificate</p>
                        <p className="text-sm text-gray-500">
                          {previewError ? 'Evidence retrieval needs review' : trainingItem.verified ? 'Previously verified' : 'Awaiting verification'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant={previewUrl ? "default" : "outline"}
                        onClick={handleViewCertificate}
                        disabled={previewLoading}
                        data-testid="view-certificate-btn"
                      >
                        {previewLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                        ) : (
                          <Eye className="h-4 w-4 mr-1.5" />
                        )}
                        {previewUrl ? 'Reload' : 'View'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleDownloadCertificate}
                        data-testid="download-certificate-btn"
                      >
                        <Download className="h-4 w-4 mr-1.5" />
                        Download
                      </Button>
                    </div>
                  </div>
                </div>
                
                {trainingItem.verified && !previewError && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    <span className="text-emerald-700 text-sm">
                      Certificate verified by admin
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No certificate uploaded</p>
                <p className="text-sm mt-1">Upload a certificate to verify this training</p>
                {/* Show warning if verified without evidence */}
                {trainingItem.verified && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200 text-left">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-800 font-medium text-sm">Verification Issue</p>
                        <p className="text-amber-700 text-xs mt-1">
                          This training is marked as verified but has no certificate uploaded. 
                          Please upload evidence to maintain CQC compliance.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : !Array.isArray(history) || history.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No history available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((item, idx) => (
                  <div 
                    key={item.id || idx}
                    className="p-3 bg-gray-50 rounded-lg border text-sm"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">
                        {item.action?.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatBackendDate(item.created_at, { format: 'medium' })}
                      </span>
                    </div>
                    {item.performed_by_name && (
                      <p className="text-gray-600">By: {item.performed_by_name}</p>
                    )}
                    {item.notes && (
                      <p className="text-gray-500 mt-1">{item.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        
        <SheetFooter className="mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </SheetFooter>
        
        {/* P0 FIX: Re-extract Preview Modal */}
        {showReExtractPreview && reExtractedTrainings && (
          <div className="fixed inset-0 z-[10050] bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">Re-extracted Training Data</h3>
                <p className="text-sm text-gray-500">
                  Certificate: {reExtractedTrainings.original_filename} • 
                  Found {reExtractedTrainings.total_extracted} training(s)
                </p>
                {reExtractedTrainings.updates_count > 0 && (
                  <p className="text-sm text-amber-600 mt-1">
                    ⚠️ {reExtractedTrainings.updates_count} record(s) will update existing data
                  </p>
                )}
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left w-8">
                        <input
                          type="checkbox"
                          checked={selectedReExtracted.length === reExtractedTrainings.trainings.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedReExtracted(reExtractedTrainings.trainings.map((_, i) => i));
                            } else {
                              setSelectedReExtracted([]);
                            }
                          }}
                        />
                      </th>
                      <th className="p-2 text-left">Training Name</th>
                      <th className="p-2 text-left">Completed</th>
                      <th className="p-2 text-left">Expires</th>
                      <th className="p-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reExtractedTrainings.trainings.map((training, idx) => (
                      <tr key={idx} className={cn(
                        "border-b hover:bg-gray-50",
                        training.is_optional && "opacity-60"
                      )}>
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selectedReExtracted.includes(idx)}
                            onChange={() => toggleReExtractSelection(idx)}
                          />
                        </td>
                        <td className="p-2">
                          <span className="font-medium">{training.training_name}</span>
                          {training.is_optional && (
                            <Badge className="ml-2 bg-gray-100 text-gray-600 text-[10px]">Optional</Badge>
                          )}
                        </td>
                        <td className="p-2">{training.completion_date || '-'}</td>
                        <td className="p-2">{training.expiry_date || '-'}</td>
                        <td className="p-2">
                          {training.is_update ? (
                            <Badge className="bg-amber-100 text-amber-700">Update</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">New</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="p-4 border-t bg-gray-50 flex justify-between">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReExtractPreview(false);
                    setReExtractedTrainings(null);
                    setSelectedReExtracted([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveReExtracted}
                  disabled={loading || selectedReExtracted.length === 0}
                  data-testid="save-reextracted-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Submit {selectedReExtracted.length} for Review
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

